// The grouped-trips table shared by the full trips list (Trips.jsx), the
// item page's Upcoming Trips card (TripsSummary.jsx) and the fleet-wide
// Trips page (AllTrips.jsx) -- one row per trip group, identical everywhere
// it appears. `groups` come already grouped and sorted (each page owns its
// own filter/sort/cap); rows open the trip-group page via `onDetails(group)`.
//
// `showItem` (default off) adds an Item column after Dates, for a list that
// mixes several items' trips. Each group then carries the item it belongs to
// as `group.item` -- a tag the fleet page attaches after grouping, which is
// why the row key becomes `<item id>:<group key>`: a group key ('n:<name>',
// 't:<leg id>') is only unique within ONE item, and two boats can both have a
// trip called "San Juans". Off, every existing caller renders exactly the
// element tree it always did.
//
// The row keeps its role="button" even though it wraps a Details <Button>
// (and, with showItem, a Link) -- pre-existing, and dropping it would touch
// every caller's tree, so it is left for a change of its own.

import Table from 'react-bootstrap/Table';
import Button from 'react-bootstrap/Button';
import { Link } from 'react-router-dom';
import { tripGroupDateRange, tripGroupSummary } from '../../fleet-shared/TripGroups.mjs';
import { urlForItem } from '../../fleet-shared/Urls.mjs';

// One grouped-trip row (as a <tr> inside the parent <Table>).
// `group.name` needs no treatment here: an unnamed group's from-to name was
// already resolved by groupTrips, which is the only place that can still tell
// it apart from a name the user typed.
const GroupRow = ({ group, placeIndex, onDetails, showItem }) => (
  <tr onClick={() => onDetails(group)} role="button" style={{ cursor: 'pointer' }}>
    <td className="align-middle entered-data text-nowrap">{tripGroupDateRange(group)}</td>
    {showItem && (
      <td className="align-middle entered-data">
        {/* stopPropagation: the link goes to the ITEM, the row to the trip. */}
        <Link to={urlForItem(group.item?.id)} onClick={(e) => e.stopPropagation()}>
          {group.item?.name}
        </Link>
      </td>
    )}
    <td className="align-middle entered-data">{group.name}</td>
    <td className="align-middle entered-data">{tripGroupSummary(group, { placeIndex })}</td>
    <td className="align-middle text-center">
      {group.hasNotes && (
        <i className="fa-solid fa-book" title="Has notes" aria-label="Has notes" role="img"></i>
      )}
    </td>
    {/* An arrow rather than the word "More": the whole row is already a link
        to the same page, so this button is an affordance rather than a label,
        and the ~50px it gives back is width a phone does not have to spare.
        Same treatment as ItemRow's Details button. */}
    <td className="align-middle text-end" style={{ width: 56 }}>
      <Button
        variant="outline-primary"
        size="sm"
        aria-label="Details"
        onClick={(e) => { e.stopPropagation(); onDetails(group); }}
      >
        <i className="fa-solid fa-chevron-right" aria-hidden="true"></i>
      </Button>
    </td>
  </tr>
);

const TripGroupsTable = ({ groups, placeIndex, onDetails, showItem = false }) => (
  <Table responsive hover className="align-middle mb-0">
    <thead className="bg-body-tertiary">
      <tr>
        <th scope="col">Dates</th>
        {showItem && <th scope="col">Item</th>}
        <th scope="col">Trip</th>
        <th scope="col">Distance</th>
        <th scope="col" className="text-center">Notes</th>
        <th scope="col" className="text-end">Details</th>
      </tr>
    </thead>
    <tbody>
      {groups.map((group) => (
        <GroupRow
          key={showItem ? `${group.item?.id}:${group.key}` : group.key}
          group={group}
          placeIndex={placeIndex}
          onDetails={onDetails}
          showItem={showItem}
        />
      ))}
    </tbody>
  </Table>
);

export default TripGroupsTable;
